/**
 * Orquestrador da tela de perfil do aluno (visão do personal).
 *
 * Antes do refactor [AÇ-016] esse arquivo tinha 982 linhas com TODA a
 * UI inline (avatar, info, financeiro, rotinas, modais...). Agora ele
 * só:
 *   1. Carrega o aluno
 *   2. Gerencia o estado global (form de edição, modais ativados)
 *   3. Compõe os sub-componentes de `@/components/student-profile/`
 *
 * Estado:
 *   - `student` / `form`: aluno do banco + EditForm derivado (modo
 *     edição). Quando user clica no lápis do header, alterna isEditing.
 *   - `showOnboardModal`: aberto pelo handleActivate (busca dados de
 *     onboarding + sugestão IA antes de mostrar)
 *
 * Decisões:
 *   - Cada sub-componente carrega seus próprios dados (useFeedback-
 *     History, usePayments etc) — exceto `useOnboardingSuggestion`,
 *     que vive aqui porque é usado em 2 lugares (AISuggestionSection
 *     + OnboardingModal). Evita 2 fetches da mesma sugestão.
 *   - O ScrollView esconde várias seções em modo edição porque elas
 *     não fazem sentido enquanto user edita campos básicos do aluno.
 */

import { useEffect, useState } from 'react';
import { View, Text, ScrollView, ActivityIndicator, TouchableOpacity, Alert } from 'react-native';
import { useLocalSearchParams, useRouter, useNavigation } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { supabase } from '@/lib/supabase';
import { Student, OnboardingData, OnboardingSuggestion } from '@/lib/types';
import { useStudents } from '@/hooks/useStudents';
import { useAuth } from '@/hooks/useAuth';
import { useOnboardingSuggestion } from '@/hooks/useOnboardingSuggestion';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import {
  ProfileHeader,
  InfoSection,
  TextFieldSection,
  EngagementSection,
  HistorySection,
  FeedbackSection,
  AISuggestionSection,
  DocumentsSection,
  RoutinesSection,
  FinanceSection,
  AssessmentSection,
  StatusActions,
  OnboardingModal,
  studentToForm,
  type EditForm,
} from '@/components/student-profile';

export default function StudentProfileScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const navigation = useNavigation();

  // --- Carregamento do aluno ---
  const [student, setStudent] = useState<Student | null>(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<EditForm | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // --- Fluxo de ativação ---
  const [activating, setActivating] = useState(false);
  const [showOnboardModal, setShowOnboardModal] = useState(false);
  const [onboardingData, setOnboardingData] = useState<OnboardingData | null>(null);
  const [modalSuggestion, setModalSuggestion] = useState<OnboardingSuggestion | null>(null);

  // --- Hooks compartilhados (sugestão IA vive aqui pq é usada em 2 lugares) ---
  const { suggestion, loading: suggestionLoading } = useOnboardingSuggestion(id);
  const { updateStudentStatus } = useStudents(student?.personal_id ?? '');
  const { profile } = useAuth();
  // Desktop não tem Stack header (DesktopShell usa Slot direto), então
  // o lápis do navigation.setOptions abaixo não aparece. Renderiza um
  // botão inline "Editar perfil" no ProfileHeader como fallback.
  const isDesktop = useIsDesktop();

  useEffect(() => {
    supabase.from('students').select('*').eq('id', id).single()
      .then(({ data }) => {
        if (data) {
          setStudent(data as Student);
          setForm(studentToForm(data as Student));
        }
        setLoading(false);
      });
  }, [id]);

  function toggleEdit() {
    if (isEditing) setForm(student ? studentToForm(student) : null);
    setIsEditing(e => !e);
  }

  useEffect(() => {
    // Em desktop o Expo Router renderiza um header automático mesmo quando
    // o _layout usa <Slot /> (sem Stack explícito). Pra não duplicar com o
    // botão "Editar perfil" inline do ProfileHeader, só seta headerRight
    // em mobile. Em desktop, headerRight = undefined limpa o header.
    navigation.setOptions({
      headerRight: isDesktop
        ? () => null
        : () => (
            <TouchableOpacity onPress={toggleEdit} style={{ marginRight: 4 }}>
              <Ionicons name={isEditing ? 'close-outline' : 'pencil-outline'} size={22} color="#C8102E" />
            </TouchableOpacity>
          ),
    });
  }, [isEditing, student, isDesktop]);

  async function handleSave() {
    if (!form || !student) return;
    setSaving(true);
    const updates = {
      full_name: form.full_name,
      email: form.email,
      phone: form.phone || null,
      goal: form.goal || null,
      birth_date: form.birth_date || null,
      height_cm: form.height_cm ? parseFloat(form.height_cm) : null,
      weight_kg: form.weight_kg ? parseFloat(form.weight_kg) : null,
      sex: form.sex,
      health_notes: form.health_notes || null,
      trainer_notes: form.trainer_notes || null,
      monthly_fee: form.monthly_fee ? parseFloat(form.monthly_fee) : null,
      due_day: form.due_day ? parseInt(form.due_day, 10) : 10,
    };
    const { data, error } = await supabase.from('students').update(updates).eq('id', student.id).select().single();
    setSaving(false);
    if (error) { Alert.alert('Erro ao salvar', error.message); return; }
    setStudent(data as Student);
    setIsEditing(false);
  }

  async function handleActivate() {
    if (!student) return;
    setActivating(true);
    // Busca onboarding_data e sugestão IA "fresca" antes de abrir o modal.
    const [{ data: stuData }, { data: sugData }] = await Promise.all([
      supabase.from('students').select('onboarding_data').eq('id', id).single(),
      supabase
        .from('onboarding_suggestions')
        .select('*')
        .eq('student_id', id)
        .in('status', ['pending', 'accepted'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);
    if (stuData?.onboarding_data) setOnboardingData(stuData.onboarding_data as OnboardingData);
    if (sugData) setModalSuggestion(sugData as OnboardingSuggestion);
    setActivating(false);
    setShowOnboardModal(true);
  }

  async function confirmActivate() {
    if (!student) return;
    setShowOnboardModal(false);
    setActivating(true);
    try {
      await updateStudentStatus({ id: student.id, status: 'active' });
      setStudent(s => s ? { ...s, status: 'active' } : s);
    } catch (e: unknown) {
      Alert.alert('Erro', e instanceof Error ? e.message : 'Erro ao liberar acesso');
    } finally {
      setActivating(false);
    }
  }

  function handleInactivate() {
    if (!student) return;
    Alert.alert(
      'Inativar aluno',
      'Tem certeza? O aluno perderá acesso ao app.',
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Inativar',
          style: 'destructive',
          onPress: async () => {
            setActivating(true);
            try {
              await updateStudentStatus({ id: student.id, status: 'inactive' });
              setStudent(s => s ? { ...s, status: 'inactive' } : s);
            } catch (e: unknown) {
              Alert.alert('Erro', e instanceof Error ? e.message : 'Erro ao inativar aluno');
            } finally {
              setActivating(false);
            }
          },
        },
      ],
    );
  }

  if (loading) return <ActivityIndicator style={{ flex: 1 }} color="#C8102E" />;
  if (!student || !form) return <Text style={{ margin: 20 }}>Aluno não encontrado.</Text>;

  // Helper p/ atualizar campos parciais do form sem repetir setForm em todo onChange.
  const onFormChange = (patch: Partial<EditForm>) => setForm(f => f ? { ...f, ...patch } : f);

  return (
    <ScrollView style={{ flex: 1, backgroundColor: '#F5F5F5' }} contentContainerStyle={{ padding: 20, paddingBottom: 40 }}>
      <ProfileHeader
        student={student}
        isEditing={isEditing}
        editName={form.full_name}
        onChangeName={v => onFormChange({ full_name: v })}
        showEditButton={isDesktop}
        onToggleEdit={toggleEdit}
      />

      <InfoSection
        student={student}
        form={form}
        isEditing={isEditing}
        onFormChange={onFormChange}
      />

      {!isEditing && (
        <EngagementSection
          studentId={student.id}
          studentName={student.full_name}
          studentPhone={student.phone}
        />
      )}

      {!isEditing && <HistorySection studentId={student.id} />}

      <TextFieldSection
        title="Objetivo"
        value={student.goal}
        editValue={form.goal}
        onChangeText={v => onFormChange({ goal: v })}
        isEditing={isEditing}
        placeholder="Ex: Emagrecer, Ganhar massa..."
      />

      <TextFieldSection
        title="Saúde / Restrições"
        value={student.health_notes}
        editValue={form.health_notes}
        onChangeText={v => onFormChange({ health_notes: v })}
        isEditing={isEditing}
        multiline
        emptyText="Nenhuma restrição registrada"
        placeholder="Ex: Hérnia lombar, hipertensão, gestante..."
      />

      <TextFieldSection
        title="Observações (interno)"
        value={student.trainer_notes}
        editValue={form.trainer_notes}
        onChangeText={v => onFormChange({ trainer_notes: v })}
        isEditing={isEditing}
        multiline
        emptyText="Nenhuma observação"
        placeholder="Observações privadas sobre o aluno..."
      />

      {isEditing && (
        <TouchableOpacity
          style={{ backgroundColor: '#C8102E', padding: 16, borderRadius: 10, alignItems: 'center', marginTop: 16 }}
          onPress={handleSave}
          disabled={saving}
        >
          <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 15 }}>
            {saving ? 'Salvando...' : 'Salvar alterações'}
          </Text>
        </TouchableOpacity>
      )}

      {!isEditing && (
        <TouchableOpacity
          style={{
            flexDirection: 'row', alignItems: 'center', gap: 8,
            backgroundColor: '#fff', borderRadius: 10, padding: 14, marginTop: 16,
            borderWidth: 1.5, borderColor: '#eee',
          }}
          onPress={() => router.push(`/(personal)/progress/${id}` as any)}
        >
          <Ionicons name="stats-chart-outline" size={16} color="#C8102E" />
          <Text style={{ fontSize: 14, fontWeight: '600', color: '#C8102E' }}>Ver Progresso</Text>
        </TouchableOpacity>
      )}

      {!isEditing && (
        <FeedbackSection
          studentId={student.id}
          studentName={student.full_name ?? student.email}
        />
      )}

      {!isEditing && <AISuggestionSection studentId={student.id} suggestion={suggestion} />}

      {!isEditing && <DocumentsSection studentId={student.id} />}

      {!isEditing && <RoutinesSection studentId={student.id} />}

      {!isEditing && profile && (
        <FinanceSection
          studentId={student.id}
          personalId={profile.id}
          monthlyFee={student.monthly_fee}
          dueDay={student.due_day}
        />
      )}

      {!isEditing && <AssessmentSection student={student} />}

      {!isEditing && (
        <StatusActions
          status={student.status}
          activating={activating}
          suggestionLoading={suggestionLoading}
          onActivate={handleActivate}
          onInactivate={handleInactivate}
        />
      )}

      <OnboardingModal
        visible={showOnboardModal}
        studentName={student.full_name ?? student.email}
        onboardingData={onboardingData}
        suggestion={modalSuggestion ?? suggestion}
        activating={activating}
        onClose={() => setShowOnboardModal(false)}
        onConfirm={confirmActivate}
      />
    </ScrollView>
  );
}
